import { useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUserRoles } from "@/hooks/use-user-role";
import Header from "@/components/Header";
import { Loader2, CalendarPlus } from "lucide-react";
import CustomCalendar from "@/components/CustomCalendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CalendarEventForm } from "@/components/CalendarEventForm";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { CalendarEvent, CalendarRow } from "@/types/events";

const Calendar = () => {
  const { calendarId } = useParams();
  const [calendar, setCalendar] = useState<CalendarRow | null>(null);
  const [mergedCalendarIds, setMergedCalendarIds] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { isAdmin } = useCurrentUserRoles();
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [subscriptionUrl, setSubscriptionUrl] = useState<string>('');

  useEffect(() => {
    const fetchCalendar = async () => {
      if (!calendarId) {
        setError(true);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('calendars')
        .select('*')
        .eq('id', calendarId)
        .eq('is_active', true)
        .maybeSingle();

      if (error || !data) {
        setError(true);
      } else {
        setCalendar(data);
        
        // Check if this is a breakfast or lunch calendar, and merge with the other
        const calendarNameLower = data.name.toLowerCase();
        const isBreakfast = calendarNameLower.includes('breakfast');
        const isLunch = calendarNameLower.includes('lunch');
        
        if (isBreakfast || isLunch) {
          // Find the other calendar (breakfast or lunch)
          const searchTerm = isBreakfast ? 'lunch' : 'breakfast';
          
          const { data: otherCalendar } = await supabase
            .from('calendars')
            .select('id')
            .eq('is_active', true)
            .ilike('name', `%${searchTerm}%`)
            .maybeSingle();
          
          if (otherCalendar) {
            // Merge both calendars
            setMergedCalendarIds([calendarId, otherCalendar.id]);
          } else {
            // Only use the current calendar
            setMergedCalendarIds(null);
          }
        } else {
          setMergedCalendarIds(null);
        }
        
        // Extract calendar ID from google_calendar_url for subscription link (robust parsing)
        try {
          // 1) Extract iframe src URL
          const attrSrcMatch = data.google_calendar_url.match(/src=["']([^"']+)["']/);
          const embedUrl = attrSrcMatch ? attrSrcMatch[1] : '';

          // 2) From the embed URL, read the ?src= calendar id param
          let calIdRaw = '';
          if (embedUrl) {
            try {
              const u = new URL(embedUrl);
              calIdRaw = u.searchParams.get('src') || '';
            } catch {
              calIdRaw = embedUrl.match(/[?&]src=([^&]+)/)?.[1] || '';
            }
          } else {
            calIdRaw = data.google_calendar_url.match(/[?&]src=([^&]+)/)?.[1] || '';
          }

          if (calIdRaw) {
            const decodedId = decodeURIComponent(calIdRaw);
            const fullId = decodedId.includes('@') ? decodedId : `${decodedId}@group.calendar.google.com`;
            const cidParam = encodeURIComponent(fullId); // URL-encode raw calendar ID
            setSubscriptionUrl(`https://calendar.google.com/calendar/u/0?cid=${cidParam}`);
          }
        } catch (e) {
          console.warn('Failed to parse subscription URL', e);
        }
      }
      setLoading(false);
    };

    fetchCalendar();
  }, [calendarId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !calendar) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="h-screen flex flex-col bg-lime-soft">
      <Header />
      <main className="flex-1 pb-4 px-4 overflow-hidden pt-[120px] sm:pt-[150px] md:pt-[170px] lg:pt-[194px]">
        <div className="h-full flex flex-col">
          <div className="flex-1 overflow-auto pt-4">
            <CustomCalendar 
              key={refreshKey}
              calendarId={mergedCalendarIds ? undefined : (calendarId as string)}
              calendarIds={mergedCalendarIds || undefined}
              isAdmin={isAdmin}
              onEventEdit={(event) => setEditingEvent(event as CalendarEvent)}
              onEventDelete={setDeletingEventId}
            />
          </div>
          <div className="relative flex items-center justify-between text-xs text-muted-foreground mt-2 px-2">
            {subscriptionUrl && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={subscriptionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center h-auto p-0 hover:underline"
                      style={{ color: 'hsl(var(--cha-logo-dark))' }}
                    >
                      <CalendarPlus className="h-3 w-3 mr-1" />
                      Add to your Google Calendar
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Subscribe to this calendar in your Google Calendar</p>
                    <p className="mt-1 break-all max-w-[70vw] text-[10px]">
                      {subscriptionUrl}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-4 text-sm">
              <span className="font-semibold text-muted-foreground">Color Key:</span>
              <div className="flex items-center gap-1">
                <span className="text-xs" style={{ color: '#F97316' }}>●</span>
                <span style={{ color: '#F97316' }}>Breakfast</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs" style={{ color: '#DC2626' }}>●</span>
                <span style={{ color: '#DC2626' }}>Lunch</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs" style={{ color: '#059669' }}>●</span>
                <span style={{ color: '#059669' }}>Snack</span>
              </div>
            </div>
            <span className="ml-auto">v2.1.0 • Updated {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </main>

      <Dialog open={!!editingEvent} onOpenChange={(open) => !open && setEditingEvent(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
          </DialogHeader>
          {editingEvent && (
            <CalendarEventForm
              event={editingEvent}
              onSuccess={() => {
                setEditingEvent(null);
                setRefreshKey(prev => prev + 1);
              }}
              onCancel={() => setEditingEvent(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingEventId} onOpenChange={(open) => !open && setDeletingEventId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  const { error } = await supabase
                    .from('imported_calendar_events')
                    .delete()
                    .eq('id', deletingEventId);

                  if (error) throw error;

                  toast.success('Event deleted successfully');
                  setRefreshKey(prev => prev + 1);
                } catch (error) {
                  console.error('Error deleting event:', error);
                  toast.error('Failed to delete event');
                } finally {
                  setDeletingEventId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Calendar;
